import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { Shield, Loader2, AlertCircle } from "lucide-react";
import { logger } from "@/lib/logger";
import { useMFA } from "@/contexts/MFAContext";

interface VerifyMFAProps {
  onVerified: () => void;
  onCancel?: () => void;
}

export function VerifyMFA({ onVerified, onCancel }: VerifyMFAProps) {
  const { enrolledFactors, refreshMFAStatus } = useMFA();

  const [verifyCode, setVerifyCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [selectedFactorIndex, setSelectedFactorIndex] = useState<number>(0);

  // Get all verified TOTP factors
  const totpFactors = enrolledFactors.filter(
    (f) => f.factor_type === "totp" && f.status === "verified"
  );

  // Get the currently selected TOTP factor
  const totpFactor = totpFactors[selectedFactorIndex] || totpFactors[0];

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    if (!totpFactor) {
      setError("No authenticator found. Please set up MFA first.");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      logger.debug("VerifyMFA: Creating challenge for factor:", totpFactor.id);

      // Create a challenge
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId: totpFactor.id });

      if (challengeError) {
        logger.error("VerifyMFA: Challenge error:", challengeError);
        throw challengeError;
      }

      logger.debug("VerifyMFA: Challenge created:", challengeData.id);

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) {
        logger.error("VerifyMFA: Verification error:", verifyError);
        throw verifyError;
      }

      logger.debug("VerifyMFA: MFA verified successfully, session elevated to AAL2");

      // Refresh MFA status in context
      await refreshMFAStatus();

      // Notify parent
      onVerified();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Verification failed";

      // Provide user-friendly error messages
      if (message.includes("Invalid") || message.includes("invalid")) {
        setError("Invalid code. Please check your authenticator app and try again.");
      } else if (message.includes("expired")) {
        setError("Code expired. Please enter the current code from your app.");
      } else {
        setError(message);
      }

      // Clear the code on error so user can retry
      setVerifyCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle auto-submit when 6 digits entered
  useEffect(() => {
    if (verifyCode.length === 6 && !isVerifying && totpFactor) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyCode]);

  if (!totpFactor) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-8 w-8 text-destructive mb-4" />
          <p className="text-center text-muted-foreground">
            No authenticator found. Please set up two-factor authentication first.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">Two-Factor Authentication</CardTitle>
        <CardDescription>
          Enter the code from your authenticator app to continue
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Factor selector - only show if user has multiple authenticators */}
        {totpFactors.length > 1 && (
          <div className="space-y-2">
            <p className="text-sm text-center text-muted-foreground">
              Select authenticator:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {totpFactors.map((factor, index) => (
                <button
                  key={factor.id}
                  onClick={() => {
                    setSelectedFactorIndex(index);
                    setVerifyCode("");
                    setError("");
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    selectedFactorIndex === index
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted border-input"
                  }`}
                >
                  {factor.friendly_name || `Authenticator ${index + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Verification Code Input */}
        <div className="space-y-3">
          <p className="text-sm text-center text-muted-foreground">
            {totpFactors.length > 1
              ? `Enter the code from "${totpFactor?.friendly_name || 'your authenticator'}"`
              : "Open your authenticator app and enter the 6-digit code"}
          </p>

          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={verifyCode}
              onChange={setVerifyCode}
              disabled={isVerifying}
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Codes refresh every 30 seconds
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleVerify}
            disabled={verifyCode.length !== 6 || isVerifying}
            className="w-full"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </Button>

          {onCancel && (
            <Button variant="ghost" onClick={onCancel} disabled={isVerifying}>
              Cancel
            </Button>
          )}
        </div>

        {/* Help text */}
        <div className="pt-4 border-t">
          <p className="text-xs text-center text-muted-foreground">
            Having trouble? Make sure your device time is correct and try entering
            the newest code from your authenticator app.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
